/**
 * JWT verification for WorkOS AuthKit tokens.
 *
 * Used by backend middleware — the platform's only JWT verifier — to verify OAuth 2.1
 * access tokens issued by AuthKit.
 */

import { jwtVerify, createRemoteJWKSet, decodeJwt } from 'jose';
import type { JWTPayload, JWTVerifyOptions } from 'jose';

export interface AuthKitJwtPayload {
  /** WorkOS user ID (JWT `sub` claim) */
  sub: string;
  /** User email */
  email?: string;
  /** WorkOS organization ID — present in JWT but ignored by all auth flows (org resolved from UserDO instead) */
  org_id?: string;
  /** WorkOS session ID (JWT `sid` claim) — identifies the device/session for revocation. */
  session_id?: string;
}

// Module-level JWKS cache — one per isolate, reused across requests.
// `createRemoteJWKSet` handles HTTP caching and key rotation internally.
const jwksCache = new Map<string, ReturnType<typeof createRemoteJWKSet>>();

function getJWKS(jwksUrl: string) {
  let jwks = jwksCache.get(jwksUrl);
  if (!jwks) {
    jwks = createRemoteJWKSet(new URL(jwksUrl));
    jwksCache.set(jwksUrl, jwks);
  }
  return jwks;
}

// WorkOS issues access tokens from two surfaces our clients use, with DIFFERENT issuers
// and JWKS endpoints:
//  - AuthKit-hosted / Connect OAuth (CLI `wayai login`, MCP clients): `iss` = the AuthKit
//    domain; keys at `${authkitDomain}/oauth2/jwks`.
//  - User Management API (web authkit-nextjs, native mobile sign-in): `iss` =
//    `https://api.workos.com/user_management/<client_id>`; keys at
//    `https://api.workos.com/sso/jwks/<client_id>`.
const USER_MANAGEMENT_ISS_PREFIX = 'https://api.workos.com/user_management/';

/**
 * Select the JWKS + expected issuer to verify against, from the token's UNVERIFIED `iss`.
 *
 * Safe by construction: the unverified `iss` only chooses WHICH known WorkOS keyset to verify
 * against — the `jwtVerify` signature + issuer check below is what actually authenticates the
 * token. User Management tokens are accepted ONLY for OUR own `userManagementClientId` (an
 * exact-string issuer match against trusted config), so a token from any other WorkOS client
 * never routes to the SSO JWKS — it falls through to the AuthKit-domain keyset and fails the
 * signature check there.
 */
function selectVerification(
  token: string,
  authkitDomain: string,
  userManagementClientId?: string,
): { jwksUrl: string; issuer: string } {
  if (userManagementClientId) {
    let iss: string | undefined;
    try {
      iss = decodeJwt(token).iss;
    } catch {
      iss = undefined;
    }
    if (iss === `${USER_MANAGEMENT_ISS_PREFIX}${userManagementClientId}`) {
      return {
        // client_id comes from trusted env config, not the token — no injection into the URL.
        jwksUrl: `https://api.workos.com/sso/jwks/${userManagementClientId}`,
        issuer: iss,
      };
    }
  }
  return { jwksUrl: `${authkitDomain}/oauth2/jwks`, issuer: authkitDomain };
}

/**
 * Verify a JWT issued by WorkOS AuthKit.
 *
 * Audience binding (RFC 8707 Resource Indicators): `expectedAudience` currently has NO
 * production caller. It existed for the MCP Worker's OAuth lane, which was removed when
 * `apps/mcp` became harness-`way_`-token-only; the backend — now the only verifier —
 * deliberately omits it, because every consumer resolves the same grants from the same
 * `sub` via `buildUserGrants`, so there is no privilege escalation vector. Treat the
 * parameter as an available binding, never as a protection something relies on.
 *
 * @param token - The raw JWT string
 * @param authkitDomain - AuthKit domain (e.g., `https://<subdomain>.authkit.app`)
 * @param expectedAudience - If provided, requires the `aud` claim to match. No caller today.
 * @param userManagementClientId - If provided, ALSO accepts User Management API access tokens
 *   for that client (`iss = https://api.workos.com/user_management/<client_id>`, verified
 *   against `https://api.workos.com/sso/jwks/<client_id>`). Used by the backend so native /
 *   SPA sign-ins are accepted; omit it (the default) to accept AuthKit-domain tokens only.
 * @returns Parsed payload with user identity
 * @throws On invalid, expired, malformed, or audience-mismatched tokens
 */
export async function verifyAuthKitJwt(
  token: string,
  authkitDomain: string,
  expectedAudience?: string,
  userManagementClientId?: string,
): Promise<AuthKitJwtPayload> {
  const { jwksUrl, issuer } = selectVerification(token, authkitDomain, userManagementClientId);
  const jwks = getJWKS(jwksUrl);

  const options: JWTVerifyOptions = { issuer };
  if (expectedAudience !== undefined) options.audience = expectedAudience;

  const { payload } = await jwtVerify(token, jwks, options);

  if (!payload.sub) {
    throw new Error('JWT missing sub claim');
  }

  return {
    sub: payload.sub,
    email: (payload as JWTPayload & { email?: string }).email,
    org_id: (payload as JWTPayload & { org_id?: string }).org_id,
    session_id: (payload as JWTPayload & { sid?: string }).sid,
  };
}
